import { useState, useMemo } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { toast } from 'sonner'
import {
  Send, MessageSquare, Share2, PlayCircle, Users, Check, X, Clock,
  ExternalLink, Image, Trash2, AlertCircle, CheckCircle, RefreshCw,
  Search, Folder, Video, Link2, Pencil, FileText,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  useFbGroups,
  useFbPages,
  useCreateFbPost,
  useShareAccounts,
  usePagePosts,
  useCreateFbStory,
  useFbStories,
  useDeleteFbStory,
  useClearDoneStories,
  useClearFailedStories,
  useFbInvites,
  useCreateFbInvites,
  useDeleteFbInvite,
  useClearDoneInvites,
  useClearFailedInvites,
  useCreateGroupInvites,
  usePageGroupIds,
} from '@/api/hooks/useExtension'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import { statusBadge } from './shared'

interface PostarTabProps {
  selectedProfileIds: string[]
}

type Mode = 'text' | 'share' | 'story' | 'invites'

export function PostarTab({ selectedProfileIds }: PostarTabProps) {
  const { t } = useLanguage()
  const [mode, setMode] = useState<Mode>('text')

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex gap-2 flex-wrap">
        <Button variant={mode === 'text' ? 'default' : 'outline'} size="sm" className="rounded-lg gap-1.5 text-xs" onClick={() => setMode('text')}>
          <MessageSquare className="h-3.5 w-3.5" />{t('Texto')}
        </Button>
        <Button variant={mode === 'share' ? 'default' : 'outline'} size="sm" className="rounded-lg gap-1.5 text-xs border-border/40" onClick={() => setMode('share')}>
          <Share2 className="h-3.5 w-3.5" />{t('Compartilhar')}
        </Button>
        <Button variant={mode === 'story' ? 'default' : 'outline'} size="sm" className="rounded-lg gap-1.5 text-xs border-border/40" onClick={() => setMode('story')}>
          <PlayCircle className="h-3.5 w-3.5" />{t('Story')}
        </Button>
        <Button variant={mode === 'invites' ? 'default' : 'outline'} size="sm" className="rounded-lg gap-1.5 text-xs border-border/40" onClick={() => setMode('invites')}>
          <Users className="h-3.5 w-3.5" />{t('Convites')}
        </Button>
      </div>

      {mode === 'text' || mode === 'share' ? (
        <CreatePostSection mode={mode} selectedProfileIds={selectedProfileIds} />
      ) : mode === 'story' ? (
        <StorySection />
      ) : (
        <InviteSection />
      )}
    </div>
  )
}

// ─── Create Post / Share ─────────────────────────────────

function CreatePostSection({ mode, selectedProfileIds }: { mode: 'text' | 'share'; selectedProfileIds: string[] }) {
  const { t } = useLanguage()
  const hasSingleProfile = selectedProfileIds.length === 1
  const profileFilter = hasSingleProfile ? selectedProfileIds[0] : undefined
  const { data: groupsData } = useFbGroups(undefined, profileFilter)
  const { data: pagesData } = useFbPages(profileFilter)
  const createPost = useCreateFbPost()

  const [message, setMessage] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [collectionFilter, setCollectionFilter] = useState('')
  const [pageId, setPageId] = useState('')
  const [mentionEveryone, setMentionEveryone] = useState(false)
  const [commentText, setCommentText] = useState('')

  // Share mode
  const [shareAccountId, setShareAccountId] = useState('')
  const [shareUrl, setShareUrl] = useState('')
  const [shareCaption, setShareCaption] = useState('')
  const [shareSearch, setShareSearch] = useState('')
  const { data: shareAccountsData } = useShareAccounts()
  const { data: pagePostsData, isLoading: loadingPosts } = usePagePosts(shareAccountId || null)
  const shareAccounts = shareAccountsData?.accounts ?? []

  // Bulk scheduling
  const [repeatCount, setRepeatCount] = useState(1)
  const [repeatInterval, setRepeatInterval] = useState('24')

  // Page-group linking: when a page is selected, only show linked groups
  const { data: pageGroupIds } = usePageGroupIds(pageId || null)
  const linkedGroupIds = pageGroupIds ?? []

  const allGroups = groupsData?.groups.filter(g => g.isActive) ?? []
  // Split into accessible vs denied
  const accessibleGroups = allGroups.filter(g => g.accessStatus !== 'denied')
  const deniedGroups = allGroups.filter(g => g.accessStatus === 'denied')

  // When posting as a page, show only groups linked to that page
  // When posting as personal (no page), show all profile groups
  const pageFilteredGroups = pageId
    ? accessibleGroups.filter(g => linkedGroupIds.includes(g.id))
    : accessibleGroups
  const filteredGroups = collectionFilter ? pageFilteredGroups.filter(g => g.collection === collectionFilter) : pageFilteredGroups
  const availablePages = pagesData?.pages.filter(p => p.isActive) ?? []
  const targetGroupIds = selectAll ? filteredGroups.map(g => g.id) : selectedGroups

  // Must select exactly 1 profile to create posts (guard after hooks)
  if (!hasSingleProfile) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mb-3" />
        <p className="text-sm font-medium">{t('Selecione exatamente 1 perfil na sidebar para criar posts')}</p>
      </div>
    )
  }

  const handleSubmit = async () => {
    if (mode === 'text') {
      if (!message.trim()) { toast.error(t('Escreva uma mensagem')); return }
    } else {
      if (!shareUrl) { toast.error(t('Selecione um post para compartilhar')); return }
    }
    if (targetGroupIds.length === 0) { toast.error(t('Selecione ao menos um grupo')); return }

    const count = Math.max(1, repeatCount)
    let successCount = 0

    for (let i = 0; i < count; i++) {
      let schedAt = scheduledAt || undefined
      if (scheduledAt && i > 0) {
        const base = new Date(scheduledAt)
        base.setHours(base.getHours() + i * Number(repeatInterval))
        schedAt = base.toISOString().slice(0, 16)
      }

      // In share mode, resolve page name for identity switching:
      // 1. If user explicitly picked a page in "Postar como Página" dropdown → use that (handled via page_id)
      // 2. If no page picked and share mode → use the share account's name
      // 3. If "Padrão do perfil" → page_id is empty, page_name is empty → posts as personal
      const selectedShareAccount = shareAccounts.find(a => a.id === shareAccountId)
      const sharePageName = (!pageId && mode === 'share') ? selectedShareAccount?.name : undefined

      const payload = mode === 'text'
        ? { message: message.trim(), group_ids: targetGroupIds, image_url: imageUrl.trim() || undefined, scheduled_at: schedAt, page_id: pageId || undefined, mention_everyone: mentionEveryone || undefined, comment_text: commentText.trim() || undefined }
        : { message: shareCaption.trim(), group_ids: targetGroupIds, share_url: shareUrl, scheduled_at: schedAt, page_id: pageId || undefined, page_name: sharePageName, mention_everyone: mentionEveryone || undefined, comment_text: commentText.trim() || undefined }

      try {
        const r = await createPost.mutateAsync(payload)
        successCount += r.count
      } catch (e: any) {
        toast.error(e.message)
        break
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} posts adicionados a fila!`)
      setMessage(''); setImageUrl(''); setScheduledAt(''); setShareUrl(''); setShareCaption(''); setCommentText('')
      setRepeatCount(1)
    }
  }

  return (
    <div className="rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-5 shadow-sm space-y-4">
      {mode === 'text' ? (
        <>
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Mensagem (suporta Spintax)')}</Label>
            <Textarea placeholder={t('Escreva sua mensagem... Use {opcao1|opcao2} para spintax')} value={message} onChange={e => setMessage(e.target.value)} rows={5} className="font-mono text-sm rounded-xl border-border/40 bg-muted/20 focus:bg-transparent transition-colors" />
            <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">{message.length} {t('caracteres')}</div>
          </div>
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Imagem (URL, opcional)')}</Label>
            <Input placeholder="https://..." value={imageUrl} onChange={e => setImageUrl(e.target.value)} className="rounded-lg border-border/40" />
          </div>
        </>
      ) : (
        <>
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Selecione a Pagina')}</Label>
            <Input
              placeholder={t('Buscar pagina...')}
              value={shareSearch}
              onChange={e => setShareSearch(e.target.value)}
              className="rounded-lg border-border/40 mb-2 h-8 text-xs"
            />
            <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
              {shareAccounts
                .filter(a => a.pageId)
                .filter(a => !shareSearch || a.name.toLowerCase().includes(shareSearch.toLowerCase()))
                .map(a => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => { setShareAccountId(a.id === shareAccountId ? '' : a.id); setShareUrl(''); setShareSearch('') }}
                  className={`text-left px-2.5 py-2 rounded-lg border text-xs truncate transition-all duration-150 ${
                    shareAccountId === a.id
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-border/40 hover:bg-muted/30'
                  }`}
                >
                  {a.name}
                </button>
              ))}
            </div>
            {shareAccountId && (
              <p className="text-[10px] text-primary mt-1.5 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                {shareAccounts.find(a => a.id === shareAccountId)?.name}
              </p>
            )}
          </div>
          {shareAccountId && (
            <div>
              <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Selecione o Post')}</Label>
              {loadingPosts ? (
                <Skeleton className="h-32 w-full rounded-xl" />
              ) : pagePostsData?.posts && pagePostsData.posts.length > 0 ? (
                <div className="max-h-48 overflow-y-auto rounded-xl border border-border/40 divide-y divide-border/30">
                  {pagePostsData.posts.map(post => (
                    <button
                      key={post.id}
                      type="button"
                      onClick={() => setShareUrl(post.permalink_url || '')}
                      className={`w-full text-left p-2.5 flex gap-2.5 transition-all duration-200 ${shareUrl === post.permalink_url ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-muted/30'}`}
                    >
                      {post.full_picture ? (
                        <img src={post.full_picture} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted/40 flex items-center justify-center shrink-0">
                          <Image className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs line-clamp-2">{post.message || t('(sem texto)')}</p>
                        {post.created_time && <span className="text-[10px] text-muted-foreground tabular-nums">{new Date(post.created_time).toLocaleDateString('pt-BR')}</span>}
                      </div>
                      {shareUrl === post.permalink_url && <Check className="h-4 w-4 text-primary shrink-0 self-center" />}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground text-center py-6">{t('Nenhum post encontrado')}</div>
              )}
              {shareUrl && (
                <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
                  <ExternalLink className="h-3 w-3 shrink-0" />
                  <span className="truncate">{shareUrl}</span>
                </div>
              )}
            </div>
          )}
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Legenda (opcional)')}</Label>
            <Textarea placeholder={t('Texto acima do post compartilhado... Suporta spintax')} value={shareCaption} onChange={e => setShareCaption(e.target.value)} rows={3} className="font-mono text-sm rounded-xl border-border/40 bg-muted/20 focus:bg-transparent transition-colors" />
          </div>
        </>
      )}

      {/* Engagement extras */}
      <div className="rounded-xl border border-border/30 bg-muted/10 p-3.5 space-y-3">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Engajamento extra')}</p>
        <div className="flex items-center gap-2.5">
          <Switch checked={mentionEveryone} onCheckedChange={setMentionEveryone} />
          <div>
            <Label className="text-xs font-medium cursor-pointer" onClick={() => setMentionEveryone(!mentionEveryone)}>@Todos</Label>
            <p className="text-[10px] text-muted-foreground">{t('Menciona todos os membros do grupo')}</p>
          </div>
        </div>
        <div>
          <Label className="mb-1.5 text-[11px] font-medium">{t('Comentario apos postar (opcional)')}</Label>
          <Textarea placeholder={t('Link ou mensagem para comentar...')} value={commentText} onChange={e => setCommentText(e.target.value)} rows={2} className="font-mono text-xs rounded-lg border-border/40 bg-muted/20 focus:bg-transparent transition-colors" />
        </div>
      </div>

      {/* Page identity */}
      {availablePages.length > 0 && (
        <div>
          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Postar como Pagina (opcional)')}</Label>
          <Select value={pageId || '_default'} onValueChange={(v) => setPageId(v === '_default' ? '' : v)}>
            <SelectTrigger className="rounded-lg border-border/40"><SelectValue placeholder={t('Perfil pessoal (padrao do perfil)')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_default">{t('Padrao do perfil')}</SelectItem>
              {availablePages.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name} ({p.profileName})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Scheduling + bulk repeat */}
      <div className="space-y-3">
        <div>
          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Agendamento (opcional)')}</Label>
          <Input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="rounded-lg border-border/40" />
        </div>
        {scheduledAt && (
          <div className="rounded-xl border border-border/30 bg-muted/10 p-3 space-y-2">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Repetir agendamento')}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">{t('Repetir')}</span>
              <Input type="number" min={1} max={50} value={repeatCount} onChange={e => setRepeatCount(Number(e.target.value))} className="w-16 h-7 text-xs rounded-lg border-border/40" />
              <span className="text-xs text-muted-foreground">{t('vez(es) a cada')}</span>
              <Select value={repeatInterval} onValueChange={setRepeatInterval}>
                <SelectTrigger className="w-20 h-7 text-xs rounded-lg border-border/40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1h</SelectItem>
                  <SelectItem value="2">2h</SelectItem>
                  <SelectItem value="4">4h</SelectItem>
                  <SelectItem value="6">6h</SelectItem>
                  <SelectItem value="12">12h</SelectItem>
                  <SelectItem value="24">24h</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {repeatCount > 1 && (
              <p className="text-[10px] text-muted-foreground">
                {t('Serao criados')} {repeatCount} {t('lotes com intervalo de')} {repeatInterval}h {t('entre cada')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Groups */}
      <div>
        <Label className="mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Grupos destino')}</Label>
        {pageId && (
          <p className="text-[10px] text-primary mb-1.5 flex items-center gap-1">
            <Link2 className="h-3 w-3" />
            {linkedGroupIds.length > 0
              ? t(`Mostrando ${linkedGroupIds.length} grupo(s) vinculado(s) a esta pagina`)
              : t('Nenhum grupo vinculado a esta pagina. Vincule grupos no painel lateral.')}
          </p>
        )}
        <div className="flex gap-1.5 flex-wrap">
          <Badge variant={selectAll ? 'default' : 'outline'} className="cursor-pointer rounded-lg transition-all duration-200" onClick={() => setSelectAll(true)}>
            {t('Todos')} ({filteredGroups.length})
          </Badge>
          <Badge variant={!selectAll ? 'default' : 'outline'} className="cursor-pointer rounded-lg border-border/40 transition-all duration-200" onClick={() => setSelectAll(false)}>
            {t('Selecionar')}
          </Badge>
          {groupsData?.collections.map(c => (
            <Badge key={c.name} variant={collectionFilter === c.name ? 'default' : 'outline'} className="cursor-pointer rounded-lg border-border/40 transition-all duration-200" onClick={() => setCollectionFilter(collectionFilter === c.name ? '' : c.name)}>
              {c.name}
            </Badge>
          ))}
        </div>
        {!selectAll && (
          <div className="max-h-48 overflow-y-auto rounded-xl border border-border/40 p-2 space-y-0.5 mt-2">
            {filteredGroups.map(g => (
              <label key={g.id} className="flex items-center gap-2.5 text-xs cursor-pointer hover:bg-muted/30 rounded-lg px-2.5 py-1.5 transition-colors">
                <input
                  type="checkbox"
                  checked={selectedGroups.includes(g.id)}
                  onChange={e => {
                    if (e.target.checked) setSelectedGroups([...selectedGroups, g.id])
                    else setSelectedGroups(selectedGroups.filter(id => id !== g.id))
                  }}
                  className="accent-primary"
                />
                {g.name}
                {g.accessStatus === 'confirmed' && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 text-green-600 border-green-300 ml-1">
                    <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                    {t('verificado')}
                  </Badge>
                )}
              </label>
            ))}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground mt-1.5 tabular-nums">{targetGroupIds.length} {t('grupos selecionados')}</div>
        {deniedGroups.length > 0 && (
          <div className="mt-3 border-t pt-3">
            <p className="text-xs text-muted-foreground mb-2">
              {t('Grupos sem acesso')} ({deniedGroups.length})
            </p>
            <div className="space-y-1">
              {deniedGroups.map(g => (
                <div key={g.id} className="flex items-center gap-2 opacity-50">
                  <X className="h-3 w-3 text-destructive" />
                  <span className="text-xs line-through">{g.name}</span>
                  <Badge variant="destructive" className="text-[10px] px-1 py-0">
                    {t('sem acesso')}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Button onClick={handleSubmit} disabled={createPost.isPending} className="w-full rounded-xl gap-2">
        {mode === 'share' ? <Share2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
        {createPost.isPending ? t('Criando...') : scheduledAt ? t('Agendar Posts') : mode === 'share' ? t('Compartilhar nos Grupos') : t('Adicionar a Fila')}
      </Button>
    </div>
  )
}

// ─── Story Section ───────────────────────────────────────

function StorySection() {
  const { t } = useLanguage()
  const { data: folders } = useMediaFolders()
  const { data: accountsData } = useShareAccounts()
  const { data: storiesData } = useFbStories()
  const createStory = useCreateFbStory()
  const deleteStory = useDeleteFbStory()
  const clearDone = useClearDoneStories()
  const clearFailed = useClearFailedStories()

  const [selectedFolderId, setSelectedFolderId] = useState('')
  const [selectedMediaUrl, setSelectedMediaUrl] = useState('')
  const [selectedMediaType, setSelectedMediaType] = useState<'image' | 'video'>('image')
  const [linkUrl, setLinkUrl] = useState('')
  const [overlayText, setOverlayText] = useState('chama no zap\n\u{1F447}')
  const [textPosition, setTextPosition] = useState<'middle' | 'bottom'>('bottom')
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([])
  const [scheduledAt, setScheduledAt] = useState('')
  const [folderSearch, setFolderSearch] = useState('')

  const accounts = accountsData?.accounts || []
  const stories = storiesData?.data || []

  const mediaFolders = useMemo(() => {
    if (!folders) return []
    return folders.filter(f => f.images && f.images.length > 0)
  }, [folders])

  const filteredFolders = useMemo(() => {
    if (!folderSearch.trim()) return mediaFolders
    const q = folderSearch.toLowerCase()
    return mediaFolders.filter(f => f.name.toLowerCase().includes(q))
  }, [mediaFolders, folderSearch])

  const selectedFolder = mediaFolders.find(f => f.id === selectedFolderId)
  const mediaItems = selectedFolder?.images || []

  function toggleAccount(id: string) {
    setSelectedAccountIds(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id])
  }

  function toggleAllAccounts() {
    setSelectedAccountIds(selectedAccountIds.length === accounts.length ? [] : accounts.map(a => a.id))
  }

  async function handleSubmit() {
    if (!selectedMediaUrl) { toast.error(t('Selecione uma midia')); return }
    if (selectedAccountIds.length === 0) { toast.error(t('Selecione pelo menos uma pagina')); return }
    try {
      const result = await createStory.mutateAsync({
        account_ids: selectedAccountIds,
        media_url: selectedMediaUrl,
        media_type: selectedMediaType,
        link_url: linkUrl ? (linkUrl.match(/^https?:\/\//) ? linkUrl : `https://${linkUrl}`) : undefined,
        overlay_text: overlayText || undefined,
        text_position: overlayText ? textPosition : undefined,
        scheduled_at: scheduledAt || undefined,
      })
      toast.success(t(`${result.count} story(s) criado(s)!`))
      setSelectedMediaUrl(''); setLinkUrl(''); setOverlayText(''); setSelectedAccountIds([]); setScheduledAt('')
    } catch (e: any) {
      toast.error(e.message || t('Erro ao criar stories'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
            <PlayCircle className="h-3.5 w-3.5 text-primary" />
          </div>
          <h3 className="text-sm font-semibold">{t('Criar Story para Paginas')}</h3>
        </div>

        {/* Folder selector */}
        <div>
          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Pasta da Biblioteca')}</Label>
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input value={folderSearch} onChange={e => setFolderSearch(e.target.value)} placeholder={t(`Buscar entre ${mediaFolders.length} pastas...`)} className="h-8 pl-8 text-xs rounded-lg border-border/40 bg-muted/30 border-transparent focus:border-primary/30 focus:bg-transparent transition-colors" />
            {folderSearch && <button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" onClick={() => setFolderSearch('')}><X className="h-3 w-3" /></button>}
          </div>
          <div className="mt-1.5 max-h-[160px] overflow-y-auto rounded-xl border border-border/40">
            {filteredFolders.length === 0 ? (
              <p className="text-[10px] text-muted-foreground text-center py-4">{folderSearch ? t('Nenhuma pasta encontrada') : t('Carregando...')}</p>
            ) : filteredFolders.map(f => (
              <button key={f.id} onClick={() => { setSelectedFolderId(f.id); setFolderSearch('') }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left transition-all duration-200 ${selectedFolderId === f.id ? 'bg-primary/10 text-primary font-medium border-l-2 border-l-primary' : 'hover:bg-muted/30'}`}>
                <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">{f.images.length}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Media grid */}
        {selectedFolder && (
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Selecione a Midia')} — {selectedFolder.name} ({mediaItems.length})</Label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-1 max-h-[220px] overflow-y-auto">
              {mediaItems.map(item => (
                <button key={item.id} onClick={() => { setSelectedMediaUrl(item.url); setSelectedMediaType(item.mediaType || 'image') }} className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all duration-200 ${selectedMediaUrl === item.url ? 'border-primary ring-2 ring-primary/30 shadow-md' : 'border-transparent hover:border-muted-foreground/30'}`}>
                  {item.mediaType === 'video' ? (
                    <div className="w-full h-full bg-muted/40 flex items-center justify-center"><Video className="h-5 w-5 text-muted-foreground" /></div>
                  ) : (
                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                  )}
                  {item.mediaType === 'video' && <div className="absolute top-0.5 right-0.5"><Badge className="text-[8px] px-1 py-0 bg-blue-600 rounded-md">VID</Badge></div>}
                  {selectedMediaUrl === item.url && <div className="absolute inset-0 bg-primary/20 flex items-center justify-center"><Check className="h-5 w-5 text-primary" /></div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Selected preview */}
        {selectedMediaUrl && (
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
            {selectedMediaType === 'video' ? (
              <div className="w-12 h-12 bg-muted/40 rounded-lg flex items-center justify-center shrink-0"><Video className="h-4 w-4 text-muted-foreground" /></div>
            ) : (
              <img src={selectedMediaUrl} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium">{selectedMediaType === 'video' ? t('Video') : t('Imagem')}</span>
              <p className="text-[10px] text-muted-foreground truncate">{selectedMediaUrl.split('/').pop()}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive shrink-0" onClick={() => setSelectedMediaUrl('')}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* Link + Overlay */}
        <div>
          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Link do Sticker (opcional)')}</Label>
          <div className="flex items-center gap-2">
            <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <Input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://meusite.com" className="rounded-lg border-border/40" />
          </div>
        </div>
        <div>
          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Texto no Story (opcional)')}</Label>
          <Textarea value={overlayText} onChange={e => setOverlayText(e.target.value.slice(0, 200))} placeholder={t('Texto sobre a imagem...')} maxLength={200} rows={2} className="rounded-lg border-border/40 resize-none" />
          {overlayText && (
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] text-muted-foreground">{t('Posicao')}:</span>
              <Button variant={textPosition === 'middle' ? 'default' : 'outline'} size="sm" className="h-6 text-[10px] rounded-lg px-3" onClick={() => setTextPosition('middle')}>{t('Meio')}</Button>
              <Button variant={textPosition === 'bottom' ? 'default' : 'outline'} size="sm" className="h-6 text-[10px] rounded-lg px-3" onClick={() => setTextPosition('bottom')}>{t('Baixo')}</Button>
            </div>
          )}
        </div>

        {/* Account selector */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Paginas')}</Label>
            <Button variant="ghost" size="sm" className="h-6 text-[10px] rounded-lg" onClick={toggleAllAccounts}>
              {selectedAccountIds.length === accounts.length ? t('Desmarcar Todas') : t('Selecionar Todas')}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[160px] overflow-y-auto">
            {accounts.map(acc => (
              <label key={acc.id} className={`flex items-center gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-all duration-200 ${selectedAccountIds.includes(acc.id) ? 'border-primary/40 bg-primary/10' : 'border-border/40 hover:border-muted-foreground/30'}`}>
                <Checkbox checked={selectedAccountIds.includes(acc.id)} onCheckedChange={() => toggleAccount(acc.id)} />
                <span className="text-xs">{acc.name}</span>
              </label>
            ))}
            {accounts.length === 0 && <p className="text-xs text-muted-foreground col-span-2">{t('Nenhuma pagina com token encontrada')}</p>}
          </div>
        </div>

        {/* Schedule */}
        <div>
          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Agendar (opcional)')}</Label>
          <Input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="rounded-lg border-border/40" />
        </div>

        <Button onClick={handleSubmit} disabled={createStory.isPending || !selectedMediaUrl || selectedAccountIds.length === 0} className="w-full rounded-xl gap-2">
          <PlayCircle className="h-4 w-4" />
          {createStory.isPending ? t('Criando...') : t(`Criar ${selectedAccountIds.length} Story(s)`)}
        </Button>
      </div>

      {/* Story queue */}
      {stories.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm shadow-sm">
          <div className="flex items-center justify-between border-b border-border/30 bg-muted/10 px-5 py-3">
            <h3 className="text-sm font-semibold">{t('Fila de Stories')} ({stories.length})</h3>
            <div className="flex gap-2">
              {stories.some(s => s.status === 'done') && (
                <Button variant="outline" size="sm" className="rounded-lg border-border/40 gap-1 text-xs" onClick={() => clearDone.mutate()}>
                  <Check className="h-3 w-3" />{t('Limpar Concluidos')}
                </Button>
              )}
              {stories.some(s => s.status === 'failed') && (
                <Button variant="outline" size="sm" className="rounded-lg border-border/40 gap-1 text-xs" onClick={() => clearFailed.mutate()}>
                  <AlertCircle className="h-3 w-3" />{t('Limpar Falhos')}
                </Button>
              )}
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent bg-muted/20">
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Midia')}</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Pagina')}</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Link')}</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Status')}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stories.map(story => (
                <TableRow key={story.id} className="transition-colors hover:bg-muted/20">
                  <TableCell>
                    {story.mediaType === 'video' ? (
                      <div className="w-10 h-10 bg-muted/40 rounded-lg flex items-center justify-center"><Video className="h-4 w-4 text-muted-foreground" /></div>
                    ) : (
                      <img src={story.mediaUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{story.accountName}</TableCell>
                  <TableCell>
                    {story.linkUrl ? (
                      <a href={story.linkUrl.match(/^https?:\/\//) ? story.linkUrl : `https://${story.linkUrl}`} target="_blank" rel="noreferrer" className="text-[10px] text-blue-500 hover:underline truncate max-w-[150px] inline-flex items-center gap-1">
                        <ExternalLink className="h-3 w-3 shrink-0" />{story.linkUrl.replace(/^https?:\/\//, '').slice(0, 30)}
                      </a>
                    ) : <span className="text-[10px] text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{statusBadge(story.status)}</TableCell>
                  <TableCell>
                    {(story.status === 'pending' || story.status === 'failed') && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive transition-colors" onClick={() => deleteStory.mutate(story.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

// ─── Invite Section ──────────────────────────────────────

function InviteSection() {
  const { t } = useLanguage()
  const [inviteMode, setInviteMode] = useState<'page' | 'group'>('page')
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([])
  const [scheduledAt, setScheduledAt] = useState('')
  const [groupPostUrls, setGroupPostUrls] = useState('')

  const { data: shareAccounts } = useShareAccounts()
  const { data: pagePostsData } = usePagePosts(selectedAccountId && inviteMode === 'page' ? selectedAccountId : null)
  const { data: invitesData } = useFbInvites()
  const createInvites = useCreateFbInvites()
  const createGroupInv = useCreateGroupInvites()
  const deleteInvite = useDeleteFbInvite()
  const clearDone = useClearDoneInvites()
  const clearFailed = useClearFailedInvites()

  const accounts = shareAccounts?.accounts ?? []
  const posts = pagePostsData?.posts ?? []
  const invites = invitesData?.data ?? []
  const hasDone = invites.some(i => i.status === 'done')
  const hasFailed = invites.some(i => i.status === 'failed')

  function togglePost(id: string) {
    setSelectedPostIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  function toggleAll() {
    setSelectedPostIds(selectedPostIds.length === posts.length ? [] : posts.map(p => p.id))
  }

  function handleSubmit() {
    if (!selectedAccountId) { toast.error(t('Selecione uma pagina')); return }
    if (inviteMode === 'page') {
      if (selectedPostIds.length === 0) { toast.error(t('Selecione ao menos um post')); return }
      const selectedPosts = posts.filter(p => selectedPostIds.includes(p.id)).map(p => ({
        post_id: p.id,
        permalink_url: p.permalink_url,
        message: p.message,
        reaction_count: p.reaction_count ?? p.reactions?.summary?.total_count ?? 0,
      }))
      createInvites.mutate(
        { account_id: selectedAccountId, posts: selectedPosts, scheduled_at: scheduledAt || undefined },
        {
          onSuccess: (r) => { toast.success(t(`${r.count} convite(s) criado(s)!`)); setSelectedPostIds([]); setScheduledAt('') },
          onError: (e) => toast.error(e.message),
        },
      )
    } else {
      if (!groupPostUrls.trim()) { toast.error(t('Cole as URLs dos posts')); return }
      const urls = groupPostUrls.split('\n').map(l => l.trim()).filter(Boolean)
      createGroupInv.mutate(
        { account_id: selectedAccountId, urls, scheduled_at: scheduledAt || undefined },
        {
          onSuccess: (r) => { toast.success(t(`${r.count} convite(s) criado(s)!`)); setGroupPostUrls(''); setScheduledAt('') },
          onError: (e) => toast.error(e.message),
        },
      )
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
            <Users className="h-3.5 w-3.5 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">{t('Convidar Curtidores')}</h3>
            <p className="text-[10px] text-muted-foreground">{t('Convide quem curtiu seus posts a seguir a pagina.')}</p>
          </div>
        </div>

        {/* Mode */}
        <div className="flex gap-2">
          <Button variant={inviteMode === 'page' ? 'default' : 'outline'} size="sm" className="rounded-lg gap-1.5 text-xs" onClick={() => setInviteMode('page')}>
            <FileText className="h-3.5 w-3.5" />{t('Posts da Pagina')}
          </Button>
          <Button variant={inviteMode === 'group' ? 'default' : 'outline'} size="sm" className="rounded-lg gap-1.5 text-xs border-border/40" onClick={() => setInviteMode('group')}>
            <Users className="h-3.5 w-3.5" />{t('Posts de Grupo')}
          </Button>
        </div>

        {/* Account */}
        <div>
          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{inviteMode === 'group' ? t('Pagina destino (convidar para)') : t('Pagina')}</Label>
          <Select value={selectedAccountId} onValueChange={v => { setSelectedAccountId(v); setSelectedPostIds([]) }}>
            <SelectTrigger className="rounded-lg border-border/40"><SelectValue placeholder={t('Selecione a pagina')} /></SelectTrigger>
            <SelectContent>
              {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Group URLs */}
        {inviteMode === 'group' && selectedAccountId && (
          <div>
            <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('URLs dos posts no grupo (1 por linha)')}</Label>
            <Textarea
              placeholder={`https://www.facebook.com/groups/123/posts/456\nhttps://www.facebook.com/groups/789/posts/012`}
              value={groupPostUrls} onChange={e => setGroupPostUrls(e.target.value)} rows={4}
              className="font-mono text-xs rounded-xl border-border/40 bg-muted/20 focus:bg-transparent transition-colors"
            />
          </div>
        )}

        {/* Page posts */}
        {inviteMode === 'page' && selectedAccountId && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Posts')}</Label>
              {posts.length > 0 && (
                <Button variant="ghost" size="sm" onClick={toggleAll} className="text-[10px] h-6 rounded-lg">
                  {selectedPostIds.length === posts.length ? t('Desmarcar Todos') : t('Selecionar Todos')}
                </Button>
              )}
            </div>
            {posts.length === 0 ? (
              <p className="text-xs text-muted-foreground py-6 text-center">{t('Nenhum post encontrado')}</p>
            ) : (
              <div className="max-h-[250px] overflow-y-auto rounded-xl border border-border/40 divide-y divide-border/30">
                {posts.map(p => (
                  <label key={p.id} className="flex items-start gap-3 px-3 py-2.5 hover:bg-muted/30 cursor-pointer transition-colors">
                    <Checkbox checked={selectedPostIds.includes(p.id)} onCheckedChange={() => togglePost(p.id)} className="mt-0.5" />
                    {p.full_picture && <img src={p.full_picture} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs truncate">{p.message || t('(sem texto)')}</p>
                      <p className="text-[10px] text-muted-foreground tabular-nums">{p.created_time && new Date(p.created_time).toLocaleDateString('pt-BR')}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-xs font-semibold tabular-nums">{((p as any).reaction_count || 0).toLocaleString()}</span>
                      <p className="text-[9px] text-muted-foreground">{t('reacoes')}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Schedule */}
        <div>
          <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Agendar (opcional)')}</Label>
          <Input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="rounded-lg border-border/40" />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={!selectedAccountId || (inviteMode === 'page' ? selectedPostIds.length === 0 : !groupPostUrls.trim()) || createInvites.isPending || createGroupInv.isPending}
          className="w-full rounded-xl"
        >
          {(createInvites.isPending || createGroupInv.isPending) ? t('Criando...') : inviteMode === 'group' ? t('Criar Convites de Grupo') : t(`Criar ${selectedPostIds.length} Convite(s)`)}
        </Button>
      </div>

      {/* Invite queue */}
      {invites.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm shadow-sm">
          <div className="flex items-center justify-between border-b border-border/30 bg-muted/10 px-5 py-3">
            <h3 className="text-sm font-semibold">{t('Fila de Convites')}</h3>
            <div className="flex gap-2">
              {hasDone && (
                <Button variant="outline" size="sm" className="rounded-lg border-border/40 gap-1 text-xs" onClick={() => clearDone.mutate()}>
                  <Check className="h-3 w-3" />{t('Limpar Concluidos')}
                </Button>
              )}
              {hasFailed && (
                <Button variant="outline" size="sm" className="rounded-lg border-border/40 gap-1 text-xs" onClick={() => clearFailed.mutate()}>
                  <X className="h-3 w-3" />{t('Limpar Falhos')}
                </Button>
              )}
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent bg-muted/20">
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Post')}</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Pagina')}</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Status')}</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Progresso')}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map(inv => (
                <TableRow key={inv.id} className="transition-colors hover:bg-muted/20">
                  <TableCell className="max-w-[200px]"><p className="text-xs truncate">{inv.postPreview || inv.postId}</p></TableCell>
                  <TableCell className="text-xs">{inv.pageName}</TableCell>
                  <TableCell>{statusBadge(inv.status)}</TableCell>
                  <TableCell>
                    <div className="text-[10px] space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-green-500 font-semibold tabular-nums">{inv.invitedCount}</span>
                        <span className="text-muted-foreground">{t('convidados')}</span>
                        {inv.reactionCount > 0 && <span className="text-muted-foreground tabular-nums">/ {inv.reactionCount.toLocaleString()}</span>}
                      </div>
                      {inv.alreadyFollowingCount > 0 && <span className="text-muted-foreground">{inv.alreadyFollowingCount} {t('ja seguem')}</span>}
                    </div>
                    {inv.status === 'processing' && inv.reactionCount > 0 && (
                      <div className="mt-1 h-1.5 bg-muted/40 rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${Math.min(100, (inv.totalProcessed / inv.reactionCount) * 100)}%` }} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {(inv.status === 'pending' || inv.status === 'failed') && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive transition-colors" onClick={() => deleteInvite.mutate(inv.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
